import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Member } from '@/types';
import { useNotification } from '@/components/notifications';
import { MembersSkeleton } from '@/pages/org-settings/members/members-skeleton';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { Table } from '@/components/table';
import { columns } from '@/pages/org-settings/members/members-columns';
import { AddMemberModal } from '@/components/add-member-modal';
import { UpdateMemberModal } from '@/pages/org-settings/members/update-member-modal';
import { updateMemberRole } from '@/services/members/update-member-role';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { removeMember } from '@/services/members/remove-member';
import { PageTitle } from '@/components/page-title';
import { PageDescription } from '@/components/page-description';
import { useOrgStore } from '@/stores/org-store';
import { AuthContext } from '@/context/auth-context';

interface MembersTableContainerProps {
  organizationId?: string;
  title: string;
  description?: string;
  fetchMembers: () => Promise<{ data?: Member[]; detail?: string }>;
  onMembersUpdate?: (members: Member[]) => void;
}

export const MembersTableContainer = ({
  organizationId,
  title,
  description,
  fetchMembers,
  onMembersUpdate,
}: MembersTableContainerProps) => {
  const notification = useNotification();
  const [membersList, setMembersList] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddUserModalOpen, setAddUserModalOpen] = useState(false);
  const [isUpdateMemberModalOpen, setIsUpdateMemberModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isSaveRoleLoading, setIsSaveRoleLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { currentOrg, orgs } = useOrgStore();
  const { isAdmin } = useContext(AuthContext);

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchMembers();
      if (result?.detail) {
        notification.error(result.detail);
        setMembersList([]);
      } else {
        setMembersList(result?.data || []);
        onMembersUpdate?.(result?.data || []);
      }
    } catch (error) {
      console.error('Get members error:', error);
      notification.error('An unexpected error occurred');
      setMembersList([]);
      onMembersUpdate?.([]);
    } finally {
      setLoading(false);
    }
  }, [fetchMembers, notification, onMembersUpdate]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleEditMember = (member: Member) => {
    setSelectedMember(member);
    setIsUpdateMemberModalOpen(true);
  };

  const handleDeleteMember = (member: Member) => {
    setSelectedMember(member);
    setIsDeleteModalOpen(true);
  };

  const handleSaveRole = async (chosenRole: string, isInfraAdmin: boolean) => {
    if (!selectedMember) return;

    try {
      setIsSaveRoleLoading(true);
      const result = await updateMemberRole({
        organizationId,
        inviteId: selectedMember.invite_id,
        email: selectedMember.email,
        role: chosenRole,
        is_infra_admin: isInfraAdmin,
      });

      if (result?.detail) {
        notification.error(result.detail);
        return;
      }

      notification.success('Role was updated successfully');
      const updatedMembers = membersList.map((member) =>
        member.email === selectedMember.email
          ? { ...member, role: chosenRole, is_infra_admin: isInfraAdmin }
          : member,
      );
      setMembersList(updatedMembers);
      onMembersUpdate?.(updatedMembers);
    } catch (error) {
      console.error('Update member role error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsSaveRoleLoading(false);
      setIsUpdateMemberModalOpen(false);
      setSelectedMember(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedMember) return;

    try {
      setIsDeleteLoading(true);
      const result = await removeMember({
        inviteId: selectedMember.invite_id,
        email: selectedMember.email,
        organizationId,
      });

      if (result?.detail) {
        notification.error(result.detail);
        return;
      }

      notification.success('Member was removed successfully');
      const updatedMembers = membersList.filter(
        (member) => member.email !== selectedMember.email,
      );
      setMembersList(updatedMembers);
      onMembersUpdate?.(updatedMembers);
    } catch (error) {
      console.error('Remove member error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedMember(null);
    }
  };

  const filteredMembers = useMemo(
    () =>
      membersList.filter((member) =>
        member.email.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [membersList, searchQuery],
  );

  const userRole = useMemo(() => {
    if (!currentOrg || !orgs || orgs.length === 0) {
      return undefined;
    }
    const org = orgs.find((org) => org.orgid === currentOrg.orgid);
    return org?.user_role;
  }, [currentOrg, orgs]);

  const isOwner = useMemo(() => {
    // Explicitly handle undefined - if we don't know the role, assume not owner
    return userRole === 'owner';
  }, [userRole]);

  // Combined permission check: org owner OR platform admin can manage members
  const canManageMembers = useMemo(() => {
    return isOwner || isAdmin;
  }, [isOwner, isAdmin]);

  if (loading) {
    return <MembersSkeleton />;
  }

  return (
    <div className='w-full'>
      <div className='flex flex-col -mt-6 py-6 gap-6 sticky top-0 bg-background z-10'>
        <div>
          <PageTitle id='org-members-title' title={title} />
          {description && <PageDescription>{description}</PageDescription>}
        </div>
        <div className='flex justify-between items-start'>
          <div className='flex items-center gap-2 w-96'>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder='Search members by email...'
              aria-label='Search members by email'
            />
          </div>
          {canManageMembers && (
            <Button
              id='org-members-add-button'
              onClick={() => setAddUserModalOpen(true)}
            >
              <Plus />
              Add Member
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={columns(canManageMembers)}
        data={filteredMembers}
        loading={loading}
        meta={{
          onEditRow: canManageMembers ? handleEditMember : undefined,
          onDeleteRow: canManageMembers ? handleDeleteMember : undefined,
        }}
      />
      {canManageMembers && (
        <>
          <AddMemberModal
            isOpen={isAddUserModalOpen}
            setAddUserModalOpen={setAddUserModalOpen}
            canChooseRole={false}
            onMemberAdded={getData}
            orgId={organizationId}
          />
          {selectedMember && (
            <UpdateMemberModal
              isOpen={isUpdateMemberModalOpen}
              currentRole={selectedMember.role}
              currentIsInfraAdmin={selectedMember.is_infra_admin ?? false}
              showInfraAdmin={isAdmin}
              handleSave={handleSaveRole}
              setIsUpdateMemberModalOpen={setIsUpdateMemberModalOpen}
              isLoading={isSaveRoleLoading}
              name={selectedMember.email}
            />
          )}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            isLoading={isDeleteLoading}
            title='Remove member'
            description='Are you sure you want to remove this member? This action cannot be undone.'
            buttonLabel='Remove member'
            handleDelete={handleDelete}
            handleCancel={() => {
              setIsDeleteModalOpen(false);
              setSelectedMember(null);
            }}
          />
        </>
      )}
    </div>
  );
};
